/**
 * AppCombobox — Searchable dropdown with lazy-loading + custom scrollbar
 *
 * Drop-in replacement for SearchableCombobox with the same API,
 * plus optional async fetching via fetchOptions prop.
 *
 * Props:
 *  items / options       - static option list [{ value, label }]  (either name works)
 *  fetchOptions          - async (search, page, pageSize) => { items, hasMore }
 *  value                 - current selected value
 *  onChange              - (value) => void
 *  placeholder           - trigger text when nothing selected
 *  searchPlaceholder     - input placeholder
 *  emptyText             - text when no results
 *  allowClear            - show "None" clear row (default true)
 *  className             - wrapper class
 *  triggerClassName       - trigger button class
 *  panelClassName         - panel class
 *  pageSize              - items per page (async, default 20)
 *  disabled              - disable the trigger
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useInfiniteOptions } from '../hooks/useInfiniteOptions'

const ITEM_HEIGHT = 38
const VISIBLE_ITEMS = 6
const LIST_MAX_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS

export default function AppCombobox({
  items: staticItemsProp,
  options: optionsProp,
  fetchOptions,
  value = '',
  onChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  allowClear = true,
  className = '',
  triggerClassName = '',
  panelClassName = '',
  pageSize = 20,
  disabled = false,
  multiple = false,
  initialLabel = '',
  selectedLabelOverride = '',
  placement = 'auto',
  minSearchLength = 3,
}) {
  const [open, setOpen] = useState(false)
  const [dropUp, setDropUp] = useState(false)
  const [position, setPosition] = useState({ left: 0, top: undefined, bottom: undefined, width: 0 })
  const rootRef = useRef(null)
  const panelRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const trackRef = useRef(null)
  const thumbRef = useRef(null)

  const effectiveInitialLabel = initialLabel || selectedLabelOverride

  // Support both `items` and `options` prop names for backward compat
  const rawStaticItems = staticItemsProp ?? optionsProp ?? []
  const isAsync = typeof fetchOptions === 'function'

  // ── Data (static or async) ──────────────────────────────────────────────
  const {
    items: hookItems,
    search,
    setSearch,
    isLoading,
    hasMore,
    sentinelRef,
    reset,
    isSearchShort,
  } = useInfiniteOptions({
    fetchOptions: isAsync ? fetchOptions : undefined,
    staticOptions: isAsync ? undefined : rawStaticItems,
    pageSize,
    enabled: open,
    minSearchLength: isAsync ? minSearchLength : 0,
    // Pre-seed with the current value so the label shows immediately in edit mode
    // (before the dropdown is opened and the first page of options is fetched).
    seedOption: value && effectiveInitialLabel ? { value: String(value), label: effectiveInitialLabel } : null,
  })

  // ── For static mode, client-side filter by search ───────────────────────
  const displayItems = useMemo(() => {
    if (isAsync) {
      return isSearchShort ? [] : hookItems
    }
    const q = search.trim().toLowerCase()
    if (!q) return hookItems
    return hookItems.filter((item) =>
      String(item.label || '').toLowerCase().includes(q) ||
      String(item.subLabel || '').toLowerCase().includes(q)
    )
  }, [hookItems, search, isAsync, isSearchShort])

  // ── Selected label (look in both static + async sources) ────────────────
  const selectedLabel = useMemo(() => {
    const all = isAsync ? hookItems : rawStaticItems
    if (multiple) {
      const valArr = Array.isArray(value) ? value : []
      if (valArr.length === 0) return ''
      if (valArr.length === 1) {
        const found = all.find((item) => String(item.value) === String(valArr[0]))
        return found?.label || `${valArr.length} selected`
      }
      return `${valArr.length} selected`
    }
    const found = all.find((item) => String(item.value) === String(value))
    return found?.label || String(effectiveInitialLabel || '')
  }, [hookItems, rawStaticItems, isAsync, value, multiple, effectiveInitialLabel])

  const needsScroll = displayItems.length > VISIBLE_ITEMS

  // ── Outside click to close (checks both trigger root and portal panel) ───
  useEffect(() => {
    if (!open) return undefined
    function onDocClick(event) {
      const clickedTrigger = rootRef.current && rootRef.current.contains(event.target)
      const clickedPanel = panelRef.current && panelRef.current.contains(event.target)
      if (!clickedTrigger && !clickedPanel) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // ── Auto-focus input when opened ────────────────────────────────────────
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  // ── Auto position with Portal (handles drop up/down & fixed coordinates) ─
  const updatePosition = useCallback(() => {
    if (!rootRef.current) return
    const rect = rootRef.current.getBoundingClientRect()
    const dropdownHeight = 280
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const shouldDropUp = placement === 'top' || (placement !== 'bottom' && spaceBelow < dropdownHeight && spaceAbove > spaceBelow)

    setDropUp(shouldDropUp)
    setPosition({
      left: rect.left,
      width: rect.width,
      top: shouldDropUp ? undefined : rect.bottom + 4,
      bottom: shouldDropUp ? window.innerHeight - rect.top + 4 : undefined,
    })
  }, [placement])

  useEffect(() => {
    if (!open) return
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, updatePosition])

  // ── Reset search when closing ───────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setSearch('')
      reset()
    }
  }, [open, setSearch, reset])

  // ── Custom scrollbar sync ───────────────────────────────────────────────
  useEffect(() => {
    const list = listRef.current
    const thumb = thumbRef.current
    const track = trackRef.current
    if (!list || !thumb || !track || !needsScroll) return

    function syncThumb() {
      const { scrollTop, scrollHeight, clientHeight } = list
      if (scrollHeight <= clientHeight) return
      const trackH = track.clientHeight
      const thumbH = Math.max(24, (clientHeight / scrollHeight) * trackH)
      const maxTop = trackH - thumbH
      const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop
      thumb.style.height = `${thumbH}px`
      thumb.style.transform = `translateY(${top}px)`
    }

    syncThumb()
    list.addEventListener('scroll', syncThumb, { passive: true })
    return () => list.removeEventListener('scroll', syncThumb)
  }, [needsScroll, displayItems.length, open])

  function handleToggle() {
    if (disabled) return
    setOpen((prev) => !prev)
  }

  function handleSelect(itemValue) {
    if (disabled) return
    if (multiple) {
      if (!itemValue) {
        onChange?.([])
        setOpen(false)
        return
      }
      const valArr = Array.isArray(value) ? value : []
      if (valArr.includes(String(itemValue))) {
        onChange?.(valArr.filter((v) => v !== String(itemValue)))
      } else {
        onChange?.([...valArr, String(itemValue)])
      }
    } else {
      onChange?.(itemValue)
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`flex w-full items-center justify-between rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 text-left text-sm font-medium text-on-surface shadow-sm transition hover:border-primary/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-60 ${triggerClassName}`}
      >
        <span className={selectedLabel ? 'text-on-surface' : 'text-on-surface-variant'}>
          {selectedLabel || placeholder}
        </span>
        <span
          className="material-symbols-outlined text-base text-on-surface-variant transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          unfold_more
        </span>
      </button>

      {/* Panel rendered via Portal directly into document.body */}
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              style={{
                position: 'fixed',
                left: `${position.left}px`,
                top: position.top !== undefined ? `${position.top}px` : undefined,
                bottom: position.bottom !== undefined ? `${position.bottom}px` : undefined,
                width: `${position.width}px`,
                zIndex: 220,
              }}
              className={`overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest backdrop-blur-xl ${dropUp
                  ? 'shadow-[0_-14px_32px_rgba(15,23,42,0.2)]'
                  : 'shadow-[0_14px_32px_rgba(15,23,42,0.2)]'
                } ${panelClassName}`}
            >
              {/* Search input */}
              <div className="border-b border-outline-variant/15 px-2.5 py-2">
                <div className="flex items-center gap-2 rounded-lg border border-outline-variant/30 bg-surface-container-low px-2.5">
                  <span className="material-symbols-outlined text-base text-on-surface-variant">search</span>
                  <input
                    ref={inputRef}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full border-none bg-transparent py-1.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60"
                  />
                  {search ? (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="rounded-full p-0.5 text-on-surface-variant hover:bg-surface-container-high"
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  ) : null}
                </div>
              </div>

              {/* List */}
              <div className="relative">
                <div
                  ref={listRef}
                  className="app-dropdown-list py-1"
                  style={{
                    maxHeight: `${LIST_MAX_HEIGHT}px`,
                    overflowY: needsScroll ? 'scroll' : 'visible',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  {/* 3-character threshold hint */}
                  {isSearchShort ? (
                    <div className="flex items-center justify-center gap-1.5 px-4 py-4 text-center text-xs text-on-surface-variant/80 italic">
                      <span className="material-symbols-outlined text-sm text-primary">info</span>
                      <span>Type at least {minSearchLength} characters to search...</span>
                    </div>
                  ) : null}

                  {/* "None / Clear" option */}
                  {allowClear && !isSearchShort ? (
                    <button
                      type="button"
                      onClick={() => handleSelect('')}
                      style={{ minHeight: `${ITEM_HEIGHT}px` }}
                      className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition hover:bg-surface-container-low"
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center">
                        {(multiple ? (!value || value.length === 0) : !value) ? (
                          <span className="material-symbols-outlined text-sm text-primary">check</span>
                        ) : null}
                      </span>
                      <span className="text-on-surface-variant">None</span>
                    </button>
                  ) : null}

                  {displayItems.map((item, idx) => {
                    const isSelected = multiple
                      ? Array.isArray(value) && value.includes(String(item.value))
                      : String(item.value) === String(value)
                    const itemKey = item.key || (item.id ? `${item.value}-${item.id}` : `${item.value}-${idx}`)
                    return (
                      <button
                        key={itemKey}
                        type="button"
                        onClick={() => handleSelect(item.value)}
                        style={{ minHeight: `${ITEM_HEIGHT}px` }}
                        className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition hover:bg-surface-container-low ${isSelected ? 'font-semibold text-primary' : 'font-normal text-on-surface'
                          }`}
                      >
                        <span className="inline-flex h-4 w-4 items-center justify-center">
                          {isSelected ? (
                            <span className="material-symbols-outlined text-sm text-primary">check</span>
                          ) : null}
                        </span>
                        <span className="truncate leading-tight">{item.label}</span>
                        {item.subLabel ? (
                          <span className="ml-auto shrink-0 pl-2 text-xs text-on-surface-variant/60 font-mono">
                            {item.subLabel}
                          </span>
                        ) : null}
                      </button>
                    )
                  })}

                  {/* Sentinel for infinite scroll */}
                  {!isSearchShort ? <div ref={sentinelRef} style={{ height: 1 }} /> : null}

                  {/* Loading spinner */}
                  {isLoading ? (
                    <div className="flex items-center justify-center py-2.5">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  ) : null}

                  {/* Empty state */}
                  {displayItems.length === 0 && !isLoading && !isSearchShort ? (
                    <p className="px-4 py-3 text-center text-sm text-on-surface-variant">{emptyText}</p>
                  ) : null}
                </div>

                {/* Custom scrollbar */}
                {needsScroll ? (
                  <div
                    ref={trackRef}
                    className="pointer-events-none absolute right-[3px] bottom-1 top-1 w-[3px] rounded-full bg-outline-variant/20"
                  >
                    <div
                      ref={thumbRef}
                      className="absolute right-0 top-0 w-full rounded-full bg-primary/40 transition-opacity"
                    />
                  </div>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  )
}
